import { icons } from 'utils/icons'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { FixMeLater } from 'types'
import { NAVIGATION, TITLES } from 'locales/locales'

import CollapsiblePanelMenu from './CollepsiblePanelMenu'
import ConversationPanel from 'components/conversation-panel/ConversationPanel'
import { IRolePermissions, SettingsPermissions, TeamAndChannelPermissions, UserManagementPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { useAppSelector } from 'app/hooks'

export interface ICollapsibleSubMenu {
  url: string
  title: string
  active: boolean
}

export interface ICollapsibleProps {
  visible: boolean
  onToggleConversationPanel: () => void
}
const SettingsSidePanel = ({ visible, onToggleConversationPanel }: ICollapsibleProps) => {
  const { t } = useTranslation()
  const location = useLocation()

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
//Dashboard
  const hasDahsboardViewPermission = isFlagSetIn(
    [SettingsPermissions.VIEW_DASHBOARD],
    permissions.settings,
  )
//Settings
  const hasSystemSettingViewPermission = isFlagSetIn(
    [SettingsPermissions.VIEW_SYSTEM_SETTINGS],
    permissions.settings,
  )
  const hasPasswordSettingsPermission = isFlagSetIn(
    [SettingsPermissions.VIEW_PASSWORD_SETTINGS ],
    permissions.settings,
  )
  const hasEmailSettingsPermission = isFlagSetIn(
    [ SettingsPermissions.VIEW_EMAIL_SETTINGS],
    permissions.settings,
  )
  const hasLdapSettingsPermission = isFlagSetIn(
    [ SettingsPermissions.VIEW_LDAP_SETTINGS],
    permissions.settings,
  )
  const hasRoleSettingsPermission = isFlagSetIn(
    [SettingsPermissions.VIEW_ROLE_SETTINGS],
    permissions.settings,
  )
//Management
  const hasUserViewPermission = isFlagSetIn(
    [UserManagementPermissions.VIEW_USER_SETTINGS],
    permissions.userManagement,
  )
  const hasTeamsViewPermission = isFlagSetIn(
    [TeamAndChannelPermissions.VIEW_TEAM_SETTINGS],
    permissions.teamAndChannel,
  )
  const hasChannelOperationPermissions = isFlagSetIn(
    [TeamAndChannelPermissions.VIEW_CHANNEL_SETTINGS],
    permissions.teamAndChannel,
  )

  const settingsSubItems = [
    {
      url: '/system-settings/server-domain-management',
      title: t(TITLES.SYSTEM_SETTINGS_TITLE),
      active: location.pathname.includes('server-domain-management'),
      permission: hasSystemSettingViewPermission,
    },
    {
      url: '/system-settings/password-management',
      title: t(TITLES.PASSWORD_SETTINGS_TITLE),
      active: location.pathname.includes('password-management'),
      permission: hasPasswordSettingsPermission,
    },
    {
      url: '/system-settings/mail-management',
      title: t(TITLES.MAIL_SETTINGS_TITLE),
      active: location.pathname.includes('mail-management'),
      permission: hasEmailSettingsPermission,
    },
    {
      url: '/system-settings/ldap-management',
      title: t(TITLES.LDAP_SETTINGS_TITLE),
      active: location.pathname.includes('ldap-management'),
      permission: hasLdapSettingsPermission,
    },
    {
      url: '/system-settings/role-management',
      title: t(TITLES.ROLES_SETTINGS_TITLE),
      active: location.pathname.includes('role-management'),
      permission: hasRoleSettingsPermission,
    },
  ];

  const managementSubItems = [
    {
      url: '/system-settings/user-management',
      title: t(TITLES.USER_SETTINGS_TITLE),
      active: location.pathname.includes('user-management'),
      permission: hasUserViewPermission,
    },
    {
      url: '/system-settings/group-management',
      title: t(TITLES.TEAMS_SETTINGS_TITLE),
      active: location.pathname.includes('group-management'),
      permission: hasTeamsViewPermission,
    },
    {
      url: '/system-settings/channel-management',
      title: t(TITLES.CHANNEL_SETTINGS_TITLE),
      active: location.pathname.includes('channel-management'),
      permission: hasChannelOperationPermissions,
    },
       {
      url: '/system-settings/share-management',
      title: t(TITLES.SHARE_SETTINGS_TITLE),
      active: location.pathname.includes('share-management'),
      permission: hasChannelOperationPermissions,
    },
    {
      url: '/system-settings/newsfeed-management',
      title: t(TITLES.NEWSFEED_SETTINGS_TITLE),
      active: location.pathname.includes('newsfeed-management'),
      permission: hasChannelOperationPermissions,
    },
  ];

  const filteredSettingsSubItems = settingsSubItems.filter(item => item.permission);
  const filteredManagementSubItems = managementSubItems.filter(item => item.permission);

  const dashboardPermissions = hasDahsboardViewPermission ?
    {
      url: '/system-settings/dashboard',
      title: t(TITLES.DASHBOARD_TITLE),
      active: location.pathname.includes('dashboard'),
      icon: icons.adminDashboardIcon,
    } : null

  const systemSettingsPermissions = [hasSystemSettingViewPermission,
    hasPasswordSettingsPermission,
    hasEmailSettingsPermission,
    hasLdapSettingsPermission,
    hasRoleSettingsPermission].some(permission => permission) ?
    {
      url: '/system-settings/server-domain-management',
      title: t(TITLES.SETTINGS_TITLE),
      active: location.pathname.includes('server-domain-management'),
      icon: icons.adminSettingsIcon,
      sub: filteredSettingsSubItems.map(({ permission, ...rest }) => rest),
    } : null

  const managementPermissions = [hasUserViewPermission,
    hasTeamsViewPermission,
    hasChannelOperationPermissions].some(permission => permission) ? {
    url: 'management',
    title: t(TITLES.MANAGEMENT_TITLE),
    active: location.pathname.includes('management'),
    icon: icons.managementIcon,
    sub: filteredManagementSubItems.map(({ permission, ...rest }) => rest),
  } : null


  const menuItem = [
    ...(dashboardPermissions ? [dashboardPermissions] : []),
    ...(systemSettingsPermissions ? [systemSettingsPermissions] : []),
    ...(managementPermissions ? [managementPermissions] : []),
  ];

  const onHideConversationPanel = () => {
    if (visible) {
      onToggleConversationPanel()
    }
  }

  return (
    <Wrapper>
      <ConversationPanel
        title={t(NAVIGATION.SETTINGS)}
        visible={visible}
        isGroup={false}
        content={menuItem.map((item: FixMeLater, index: number) => {
          return (
            <CollapsiblePanelMenu
              onToggleConversationPanel={onHideConversationPanel}
              key={index}
              url={item.url}
              active={item.active}
              title={item.title}
              sub={item.sub}
              icon={item.icon}
            />
          )
        })}
        onHideConversationPanel={onToggleConversationPanel}
      />
    </Wrapper>
  )
}

export default SettingsSidePanel

export const Wrapper = styled.div`
  z-index: 5;
`
